import { Star } from 'lucide-react';
import { cn } from '../../lib/utils';

interface StarRatingProps {
  /** Null is "no vote yet": every star is off. Votes run from 0.5 to 5. */
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  className?: string;
  /** Accessible name of the star that gives `count` stars, e.g. "3 stars". */
  starLabel?: (count: number) => string;
}

export function StarRating({ value, onChange, disabled = false, className, starLabel }: StarRatingProps) {
  const stars = [1, 2, 3, 4, 5];

  const handleClick = (starIndex: number, event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    // A click from the keyboard has no pointer position (detail is 0), and
    // reading one would always land on the left half: it gives the whole star.
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const isLeftHalf = event.detail !== 0 && clickX < rect.width / 2;

    const newValue = isLeftHalf ? starIndex - 0.5 : starIndex;

    // Clicking the vote already given takes it back: no vote, never 0.
    onChange(newValue === value ? null : newValue);
  };

  const current = value ?? 0;

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      {stars.map((starIndex) => {
        const filled = current >= starIndex;
        const halfFilled = !filled && current >= starIndex - 0.5;

        return (
          <button
            key={starIndex}
            type="button"
            disabled={disabled}
            onClick={(e) => handleClick(starIndex, e)}
            aria-label={starLabel?.(starIndex)}
            aria-pressed={filled || halfFilled}
            className={cn(
              'relative p-0.5 transition-colors',
              disabled ? 'cursor-default' : 'cursor-pointer hover:scale-110'
            )}
          >
            {halfFilled ? (
              <div className="relative">
                <Star className="h-6 w-6 text-muted-foreground/30" />
                <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                  <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                </div>
              </div>
            ) : (
              <Star
                className={cn(
                  'h-6 w-6',
                  filled
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-muted-foreground/30'
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
